import React, { useEffect, useState } from "react";
import { IonPage, IonContent, IonItem, IonImg, IonLoading, IonGrid, IonRow, IonCol} from '@ionic/react';
import './PinLockPage.css';
import { useTranslation } from 'react-i18next';
import { useHistory } from "react-router-dom";
import { useAppDispatch, useAppSelector } from '../../../services/redux/hook';
import KeyPad from "../../../components/KeyPad/KeyPad"; // Import the updated KeyPad
import cn from 'classnames';
import { WalletInfoState } from "../../../types/types";
import { Networks } from "@stellar/stellar-sdk";
import { setWalletInfo } from "../../../services/redux/slices/walletInfo";

import { KeyManager, KeyManagerPlugins } from "../../../services/stellar/wallet-sdk";
import { CapacitorKeyStore } from "../../../services/stellar/wallet-sdk/plugins/CapacitorStorage";
import { Haptics } from "@capacitor/haptics";
import { setPIN } from "../../../services/redux/slices/pin";
import { resetSelectedAsset } from "../../../services/redux/slices/selected-asset/selected-asset";
import { resetPaymentDestinationInfo } from "../../../services/redux/slices/paymentDestinationInfo/paymentDestinationInfo";
import BiometricSecurity from "../../../services/security/biometricSecurity";
import { Capacitor } from '@capacitor/core';
import { useDeviceStorage } from "../../../services/device_storage/useDeviceStorage";

const isTestnet = import.meta.env.VITE_isTestnet === 'true';

const PinLockPage: React.FC = () => {

  const [pins, setPins] = useState(['', '', '', '', '', '']);
  const [isIncorrect, setIsIncorrect] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [hasTriedBiometric, setHasTriedBiometric] = useState(false);
  const [biometricFailed, setBiometricFailed] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const { get: getStorage } = useDeviceStorage();


  const history = useHistory();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const walletInfo: WalletInfoState = useAppSelector((state) => state.walletInfo);
  const keyManager = new KeyManager({ keyStore: new CapacitorKeyStore() });
  keyManager.registerEncrypter(KeyManagerPlugins.ScryptEncrypter);
  keyManager.setDefaultNetworkPassphrase(isTestnet ? Networks.TESTNET : Networks.PUBLIC);
  
  // Check if biometric authentication is available and trigger automatic authentication
  useEffect(() => {
    const checkBiometricAvailability = async () => {

      const biometricEnabledRaw = await getStorage('biometric_enabled');
      const biometricEnabled = biometricEnabledRaw === 'true';
      if (!biometricEnabled) {
        return;
      }
      // Skip if running in browser
      if (Capacitor.getPlatform() === 'web') {
        return;
      }
      
      const biometricSecurity = BiometricSecurity.getInstance();
      const result = await biometricSecurity.isBiometricAvailable();
      
      setBiometricAvailable(result.isAvailable);
      
      // If biometrics are available and we haven't tried yet, attempt automatic authentication
      if (result.isAvailable && !hasTriedBiometric) {
        setHasTriedBiometric(true);
        await tryBiometricAuthentication();
      }
    };
    
    checkBiometricAvailability();
  }, [hasTriedBiometric, getStorage]);

  // Attempt biometric authentication automatically
  const tryBiometricAuthentication = async () => {
    try {
      setIsAuthenticating(true);
      const biometricSecurity = BiometricSecurity.getInstance();
      
      // Check if we have stored credentials for the wallet PIN
      const credentialsResult = await biometricSecurity.getCredentials('wallet_pin');
      
      if (credentialsResult.success && credentialsResult.password) {
        // We have stored PIN, now authenticate with biometrics
        const verifyResult = await biometricSecurity.verifyIdentity({
          title: t('biometric.auth.TITLE') ?? "",
          subtitle: t('biometric.auth.SUBTITLE') ?? "",
          description: t('biometric.auth.DESCRIPTION') ?? ""
        });
        
        if (verifyResult.verified) {
          // Use the retrieved PIN to unlock the wallet
          await authenticateWithPin(credentialsResult.password);
          return;
        }
      }
    } catch (error) {
      console.log('Biometric authentication failed or cancelled:', error);
      setBiometricFailed(true);
      // Continue with PIN entry
    } finally {
      setIsAuthenticating(false);
    }
  };


  useEffect(() => {
    if (pins.every(pin => pin !== '')) {
      verifyPin();
    }
  }, [pins]);

  // Authenticate with PIN (extracted for reuse in biometric flow)
  const authenticateWithPin = async (inputCombination: string) => {
    setShowLoading(true);

    try {
      const account_key = await keyManager.loadKey("account_key", inputCombination);
      const device_key = await keyManager.loadKey("device_key", inputCombination);

      const newWalletInfo = {
        ...walletInfo,
        account_key: account_key.publicKey,
        account_private_key: account_key.privateKey,
        device_key: device_key.publicKey,
        device_private_key: device_key.privateKey,
        has_key: true
      };

      if (biometricAvailable) {
        const biometricSecurity = BiometricSecurity.getInstance();
        try {
          // Check if credentials already exist by trying to get them
          const existingCredentials = await biometricSecurity.getCredentials('wallet_pin');
          if (!existingCredentials.success || !existingCredentials.password) {
            // Store credentials only if they don't exist or are invalid
            await biometricSecurity.storeCredentials('wallet_pin', 'user', inputCombination);
          }
        } catch (error) {
          // Handle error (e.g., log it, show a message, etc.)
          console.error("Failed to store biometric credentials:", error);
          // Optionally, notify the user or take other action
        }
      }

      dispatch(setWalletInfo(newWalletInfo));
      dispatch(setPIN(inputCombination));
      setPins(['', '', '', '', '', '']);
      setShowLoading(false);
      resetInitialStates();
      history.push('/init');
    } catch {
      handleIncorrectPin();
    }
  };

  const verifyPin = async () => {
    const inputCombination = pins.join("");
    await authenticateWithPin(inputCombination);
  };

  const handleIncorrectPin = async () => {
    setShowLoading(false);
    setIsIncorrect(true);
    await Haptics.vibrate({ duration: 800 });

    setTimeout(() => {
      setPins(['', '', '', '', '', '']);
      setIsIncorrect(false);
    }, 800);
  };

  const handleClick = (value: string) => {
    const updatedPins = [...pins];
    const nextIndex = updatedPins.indexOf('');
    if (nextIndex !== -1) {
      updatedPins[nextIndex] = value;
      setPins(updatedPins);
    }
  };

  const handleRemove = () => {

    let tempCode = pins;
    for (let i = tempCode.length - 1; i >= 0; i--) {
      if (tempCode[i] != '') {
        tempCode[i] = '';
        break
      }
      else {
        continue
      }
    }
    setPins([...tempCode])


  }


  const resetInitialStates = () => {
    dispatch(resetPaymentDestinationInfo());
    dispatch(resetSelectedAsset());
  };

  return (
    <IonPage>
      <IonContent scrollY={false}>
        <IonLoading isOpen={showLoading} />

  
        <IonItem lines="none" className="pinlock-logo-container"> 
          <IonImg className="pinlock-logo-img" src="/assets/img/ebioro-symbol-darkblue.svg" />
        </IonItem>
      

        <div className="pinlock-header ion-padding">
          <h4 className="ion-text-center">{t('pin.lock-screen.GREETING_HEADER')}</h4>
          {isAuthenticating && (
            <p className="biometric-message biometric-message--authenticating">
              {t('biometric.authenticating') || 'Authenticating with biometrics...'}
            </p>
          )}
          {biometricFailed && !isAuthenticating && (
            <p className="biometric-message biometric-message--failed">
              {t('biometric.fallback_to_pin') || 'Please enter your PIN to continue'}
            </p>
          )}
        </div>

        <IonGrid className="pinlock-PIN">
          <IonRow>
            {pins.map((value, index) => (
              <IonCol size="2" key={index} className="ion-text-center">
                <div className={cn({ "animated-shake": isIncorrect })}>
                  <div className={`${value !== '' ? 'pinlock-otp-input__active' : 'pinlock-otp-input'}`} />
                </div>
              </IonCol>
            ))}
          </IonRow>
        </IonGrid>


        <IonGrid className="pinlock-pinKeyPad">
          <KeyPad handleClick={handleClick} handleRemove={handleRemove} compact />
        </IonGrid>
      </IonContent>
    </IonPage>
  );
};

export default PinLockPage;
